// What a layer of the world's cloud does to light: the one source of every cloud constant on the client.
//
// The server derives it from the world's condensates (simulation/src/atmos/cloudoptics.py: Mie for the droplets, adding-doubling for the layer) and sends it in the atmosphere
// message (`cloud_optics`). Nothing here is tuned: K (optical depth per kg of water), the share of a beam a layer scatters down, the share of the sky's light it passes, its
// reflectance, are read from the tables, and another world's droplets (or none) send other tables. Until the message arrives `ready` is false, and the places that need it keep
// the server's light, or draw no cloud, rather than guess.
//
// Two species, in the server's order: 0 liquid (the low, warm cloud), 1 ice (the high one). Per species the tables are colours (linear RGB, relative to the light that arrives):
//     beamR   (nt, nm, 3)   the share of a beam reflected up, by the layer's vertical optical depth and the beam's cosine from the zenith
//     beamTd  (nt, nm, 3)   the share of a beam that arrives below as DIFFUSE light (the unscattered beam, exp(-tau / mu), is not in it)
//     difT    (nt, 3)       isotropic light from above: the share that gets through
//     difR    (nt, 3)       isotropic light from above: the share reflected
// on warped axes: tau = tau0 * expm1(s * ln(1 + tauMax / tau0)) for s in 0..1 over nt nodes, mu = a * a for a in sqrt(muMin)..1 over nm nodes. Lookups are linear in the node index.

import * as THREE from 'three';

export const LIQUID = 0;
export const ICE = 1;
const SPECIES = 2;

export interface CloudOpticsMsg {
  version: number; species: string[];
  tau_0: number; tau_max: number; mu_min: number; nt: number; nm: number;
  k_ext: number[];
  beam_r: string; beam_td: string; dif_t: string; dif_r: string;
}

function f32(b64: string): Float32Array {
  const s = atob(b64), n = s.length, u = new Uint8Array(n);
  for (let i = 0; i < n; i++) u[i] = s.charCodeAt(i);
  return new Float32Array(u.buffer, 0, n >> 2);
}

class CloudOpticsStore {
  ready = false;
  version = 0;                                   // bumped when new tables arrive
  nt = 0; nm = 0; tau0 = 1; tauMax = 1; muMin = 0.01; private lnT = 1; private a0 = 0.1;
  kExt = [0, 0];                                 // m2/kg of the species' condensate
  private beamR: Float32Array = new Float32Array(0); private beamTd: Float32Array = new Float32Array(0); private difT: Float32Array = new Float32Array(0);
  /** For GLSL: width nm (the beam's cosine axis), height 2 * nt (liquid rows, then ice rows); rgb the beam reflectance, a the diffuse reflectance (luminance, the same along the row). Half floats, filtered linearly. */
  readonly texture: THREE.DataTexture;
  /** The uniforms of CLOUD_RESP_GLSL: one set of objects shared by every material that takes them, kept current here. */
  readonly uniforms: Record<string, THREE.IUniform> = {
    uCRp: { value: new THREE.Vector4(1, 1, 0.1, 0) },     // tau0, ln(1 + tauMax / tau0), sqrt(muMin), ready
    uCRq: { value: new THREE.Vector4(1, 1, 0, 0) },       // nm, nt, K of liquid, K of ice
  };

  constructor() {
    this.texture = new THREE.DataTexture(new Uint16Array(4), 1, 1, THREE.RGBAFormat, THREE.HalfFloatType);
    this.texture.minFilter = this.texture.magFilter = THREE.LinearFilter;
    this.texture.wrapS = this.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.texture.generateMipmaps = false;
    this.texture.needsUpdate = true;
    this.uniforms.uCR = { value: this.texture };
  }

  private sync(): void {
    (this.uniforms.uCRp!.value as THREE.Vector4).set(this.tau0, this.lnT, this.a0, this.ready ? 1 : 0);
    (this.uniforms.uCRq!.value as THREE.Vector4).set(this.nm, this.nt, this.kg(LIQUID), this.kg(ICE));
  }

  /** Take the tables of an atmosphere message. Throws on a malformed block (the caller drops the atmosphere then). */
  set(m: CloudOpticsMsg | undefined | null): void {
    if (!m) throw new Error('the atmosphere message has no cloud_optics');
    const { nt, nm } = m;
    const bR = f32(m.beam_r), bT = f32(m.beam_td), dT = f32(m.dif_t), dR = f32(m.dif_r);
    if (!(nt >= 2 && nm >= 2) || bR.length !== SPECIES * nt * nm * 3 || bT.length !== bR.length || dT.length !== SPECIES * nt * 3 || dR.length !== dT.length || m.k_ext.length !== SPECIES) {
      throw new Error('cloud_optics: table sizes disagree');
    }
    this.nt = nt; this.nm = nm; this.tau0 = m.tau_0; this.tauMax = m.tau_max; this.muMin = m.mu_min;
    this.lnT = Math.log1p(m.tau_max / m.tau_0); this.a0 = Math.sqrt(m.mu_min);
    this.kExt = [m.k_ext[0]!, m.k_ext[1]!];
    this.beamR = bR; this.beamTd = bT; this.difT = dT;
    // the texture: rgb the beam reflectance, a the diffuse reflectance as luminance
    const w = nm, h = SPECIES * nt, half = new Uint16Array(w * h * 4);
    const H = THREE.DataUtils.toHalfFloat;
    for (let s = 0; s < SPECIES; s++) for (let i = 0; i < nt; i++) {
      const dr = dR.subarray((s * nt + i) * 3, (s * nt + i) * 3 + 3), y = 0.2126 * dr[0]! + 0.7152 * dr[1]! + 0.0722 * dr[2]!;
      for (let j = 0; j < nm; j++) {
        const src = ((s * nt + i) * nm + j) * 3, dst = ((s * nt + i) * nm + j) * 4;
        half[dst] = H(bR[src]!); half[dst + 1] = H(bR[src + 1]!); half[dst + 2] = H(bR[src + 2]!); half[dst + 3] = H(y);
      }
    }
    this.texture.image = { data: half, width: w, height: h } as unknown as ImageData;
    this.texture.dispose();
    this.texture.needsUpdate = true;
    this.ready = true;
    this.version++;
    this.sync();
  }

  /** Back to not knowing (the atmosphere was refused). */
  clear(): void { this.ready = false; this.version++; this.sync(); }

  /** Optical depth per g/m2 of the species' condensate (0 until the tables are in: a cloud with no optics is not drawn thick). */
  tauPerGram(species = LIQUID): number { return this.ready ? this.kExt[species]! * 1e-3 : 0; }
  /** Optical depth per kg/m2 of water path. */
  kg(species = LIQUID): number { return this.ready ? this.kExt[species]! : 0; }

  private tauIdx(tau: number): number {
    const s = Math.log1p(Math.max(tau, 0) / this.tau0) / this.lnT;
    return Math.min(Math.max(s, 0), 1) * (this.nt - 1);
  }
  private muIdx(mu: number): number {
    const a = Math.sqrt(Math.min(Math.max(mu, this.muMin), 1));
    return (a - this.a0) / (1 - this.a0) * (this.nm - 1);
  }

  /** The scattered beam that arrives below a layer, per unit of the beam's horizontal irradiance above it (a colour into `out`). 0 until the tables are in. */
  beamToDiffuse(species: number, tau: number, mu: number, out: number[]): void {
    if (!this.ready) { out[0] = out[1] = out[2] = 0; return; }
    this.bilinear(this.beamTd, species, tau, mu, out);
  }
  /** The share of a beam a layer reflects (a colour into `out`). */
  beamReflectance(species: number, tau: number, mu: number, out: number[]): void {
    if (!this.ready) { out[0] = out[1] = out[2] = 0; return; }
    this.bilinear(this.beamR, species, tau, mu, out);
  }
  /** The share of isotropic light (the sky seen through the layer) that gets through (a colour into `out`). 1 until the tables are in. */
  diffuseTransmittance(species: number, tau: number, out: number[]): void {
    if (!this.ready) { out[0] = out[1] = out[2] = 1; return; }
    this.linear(this.difT, species, tau, out);
  }

  private bilinear(tab: Float32Array, sp: number, tau: number, mu: number, out: number[]): void {
    const ti = this.tauIdx(tau), mi = this.muIdx(mu), nt = this.nt, nm = this.nm;
    const i = Math.min(Math.floor(ti), nt - 2), j = Math.min(Math.floor(mi), nm - 2), ft = ti - i, fm = mi - j;
    const base = sp * nt * nm * 3;
    for (let c = 0; c < 3; c++) {
      const a00 = tab[base + (i * nm + j) * 3 + c]!, a01 = tab[base + (i * nm + j + 1) * 3 + c]!, a10 = tab[base + ((i + 1) * nm + j) * 3 + c]!, a11 = tab[base + ((i + 1) * nm + j + 1) * 3 + c]!;
      out[c] = (a00 * (1 - fm) + a01 * fm) * (1 - ft) + (a10 * (1 - fm) + a11 * fm) * ft;
    }
  }
  private linear(tab: Float32Array, sp: number, tau: number, out: number[]): void {
    const ti = this.tauIdx(tau), nt = this.nt, i = Math.min(Math.floor(ti), nt - 2), f = ti - i, base = sp * nt * 3;
    for (let c = 0; c < 3; c++) out[c] = tab[base + i * 3 + c]! * (1 - f) + tab[base + (i + 1) * 3 + c]! * f;
  }
}

/** The one store: the atmosphere message feeds it (as planet.ts is for the planet's radius). */
export const cloudOptics = new CloudOpticsStore();

/** GLSL for a deck of cloud seen from a shader: `deckResp(k, cov, wat, mu0)` is (beam reflectance rgb, diffuse reflectance) of the deck `k` (0 low, 1 mid: liquid; 2 high: ice) for the sun's cosine mu0
 *  against the deck's surface, from the cell's cover and in-cloud water (kg/m2). Zero until the tables are in. The uniforms (cloudOpticsUniforms) are the store's own. */
export const CLOUD_RESP_GLSL = /* glsl */ `
uniform sampler2D uCR;
uniform vec4 uCRp;               // tau0, ln(1 + tauMax / tau0), sqrt(muMin), ready (0 or 1)
uniform vec4 uCRq;               // nm, nt, K of liquid (m2/kg), K of ice
vec4 deckResp(int k, float cov, float wat, float mu0) {
  if (uCRp.w < 0.5) return vec4(0.0);
  float tau = (k == 2 ? uCRq.w : uCRq.z) * wat / max(cov, 0.03);
  float t = clamp(log(1.0 + tau / uCRp.x) / uCRp.y, 0.0, 1.0);
  float a = sqrt(clamp(mu0, uCRp.z * uCRp.z, 1.0));
  float u = (a - uCRp.z) / (1.0 - uCRp.z);
  vec2 uv = vec2((u * (uCRq.x - 1.0) + 0.5) / uCRq.x, ((k == 2 ? uCRq.y : 0.0) + t * (uCRq.y - 1.0) + 0.5) / (2.0 * uCRq.y));
  return texture2D(uCR, uv);
}
`;

/** The uniforms of CLOUD_RESP_GLSL: the store's own objects (current whenever the tables change). */
export function cloudOpticsUniforms(): Record<string, THREE.IUniform> {
  return cloudOptics.uniforms;
}
